import { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { maskDateTyping } from '../dateMask.js';

// DateField — a custom, dark-themed date picker that matches Deskte (replaces
// the browser's native <input type="date">). Drop-in compatible: it takes the
// same `value` (a "YYYY-MM-DD" string) and fires `onChange({ target: { value }})`,
// so existing handlers (e=>...e.target.value) keep working unchanged. It also
// passes through className / style / min / max / disabled / id / placeholder.
//
// Dates are built from LOCAL parts (never toISOString) to avoid the UTC
// day-shift bug, and the trigger always shows mm/dd/yyyy (en-US) per the app
// convention, regardless of UI language.

const pad = (n) => String(n).padStart(2, '0');
const parseVal = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3] };
};
const toVal = (y, mo, d) => `${y}-${pad(mo)}-${pad(d)}`;
const fmtDisplay = (v) => { const p = parseVal(v); return p ? `${pad(p.mo)}/${pad(p.d)}/${p.y}` : ''; };
const cmp = (a, b) => a < b ? -1 : a > b ? 1 : 0; // compare YYYY-MM-DD strings

const MONTHS = {
  es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};
const DOW = { es: ['D', 'L', 'M', 'M', 'J', 'V', 'S'], en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'] };

export default function DateField({ value, onChange, className = 'dkt-inp', style, min, max, disabled, id, name, placeholder, lang }) {
  const es = (lang || (typeof document !== 'undefined' && document.documentElement.lang)) === 'es';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sel = parseVal(value);
  const today = new Date();
  const todayVal = toVal(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const [view, setView] = useState(() => sel ? { y: sel.y, mo: sel.mo } : { y: today.getFullYear(), mo: today.getMonth() + 1 });

  // Keep the visible month in sync when the value changes from outside.
  useEffect(() => { const p = parseVal(value); if (p) setView({ y: p.y, mo: p.mo }); }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const fire = (v) => { onChange && onChange({ target: { value: v } }); };
  const pick = (y, mo, d) => { fire(toVal(y, mo, d)); setOpen(false); };

  // Manual typing support (Lissette 2026-06-19 #7: "déjame escribir la fecha a
  // mano, no solo el calendario"). The trigger is a real text input; typing a
  // valid mm/dd/yyyy fires onChange, and the calendar icon still opens the
  // picker. While the field is focused we don't reformat under the cursor.
  const focusedRef = useRef(false);
  const [text, setText] = useState(() => fmtDisplay(value));
  useEffect(() => { if (!focusedRef.current) setText(fmtDisplay(value)); }, [value]);
  const tryParseTyped = (s) => {
    const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(s);
    if (!m) return null;
    const mo = +m[1], d = +m[2], y = +m[3];
    if (mo < 1 || mo > 12 || d < 1 || y < 1000) return null;
    if (d > new Date(y, mo, 0).getDate()) return null;
    return toVal(y, mo, d);
  };
  // Las barras se ponen solas segun se teclea (Lissette 2026-08-25, de parte de
  // una agente). Ver `maskDateTyping` para el porque de cada regla.
  //
  // Solo se reformatea con el cursor AL FINAL. Editando por el medio, cambiar
  // el texto debajo del cursor lo manda al final en cada pulsacion, y corregir
  // el mes de una fecha ya escrita se vuelve imposible.
  const onType = (e) => {
    const crudo = e.target.value;
    const alFinal = e.target.selectionStart === null || e.target.selectionStart === crudo.length;
    const s = alFinal ? maskDateTyping(crudo, crudo.length < text.length) : crudo;
    setText(s);
    const v = tryParseTyped(s);
    if (v) fire(v);
  };
  const onBlurInput = () => {
    focusedRef.current = false;
    const s = (text || '').trim();
    if (s === '') { fire(''); return; }
    const v = tryParseTyped(s);
    if (v) { fire(v); setText(fmtDisplay(v)); } else { setText(fmtDisplay(value)); }
  };

  // Build the 6-week grid for the visible month.
  const first = new Date(view.y, view.mo - 1, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(view.y, view.mo, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const stepMonth = (delta) => setView((v) => { let mo = v.mo + delta, y = v.y; if (mo < 1) { mo = 12; y--; } if (mo > 12) { mo = 1; y++; } return { y, mo }; });
  const years = []; for (let y = today.getFullYear() + 10; y >= today.getFullYear() - 100; y--) years.push(y);
  const disabledDay = (d) => { const v = toVal(view.y, view.mo, d); return (min && cmp(v, min) < 0) || (max && cmp(v, max) > 0); };

  const triggerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: disabled ? 'default' : 'pointer', width: '100%', boxSizing: 'border-box', ...(style || {}) };
  const selStyle = { width: 'auto', padding: '.25rem .35rem', borderRadius: 7, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', fontSize: '.78rem', fontFamily: 'inherit', cursor: 'pointer' };

  return (
    <div ref={ref} style={{ position: 'relative' }} id={id}>
      <div className={className} style={triggerStyle}>
        <input
          type="text" inputMode="numeric" autoComplete="off" disabled={disabled}
          value={text}
          onChange={onType}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={onBlurInput}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
          placeholder={placeholder || 'mm/dd/yyyy'}
          style={{ flex: 1, minWidth: 0, width: '100%', border: 'none', background: 'transparent', outline: 'none', color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-mono)', fontSize: 'inherit', padding: 0 }}
        />
        <button type="button" disabled={disabled} aria-label={es ? 'Abrir calendario' : 'Open calendar'}
          onClick={() => !disabled && setOpen((o) => !o)}
          style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: disabled ? 'default' : 'pointer', display: 'flex', flexShrink: 0 }}>
          <CalendarIcon size={15} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        </button>
      </div>
      {name && <input type="hidden" name={name} value={value || ''} readOnly />}

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1000, width: 270, background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border2,#1f3f60)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.55)', padding: '.7rem' }}>
          {/* Header: prev / month + year selects / next */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '.6rem' }}>
            <button type="button" onClick={() => stepMonth(-1)} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: '.25rem', display: 'flex' }}><ChevronLeft size={15} /></button>
            <select value={view.mo} onChange={(e) => setView((v) => ({ ...v, mo: +e.target.value }))} style={{ ...selStyle, flex: 1 }}>
              {(es ? MONTHS.es : MONTHS.en).map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={view.y} onChange={(e) => setView((v) => ({ ...v, y: +e.target.value }))} style={selStyle}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button type="button" onClick={() => stepMonth(1)} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: '.25rem', display: 'flex' }}><ChevronRight size={15} /></button>
          </div>

          {/* Weekday row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 2 }}>
            {(es ? DOW.es : DOW.en).map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: '.6rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', padding: '2px 0' }}>{d}</div>)}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const v = toVal(view.y, view.mo, d);
              const isSel = sel && sel.y === view.y && sel.mo === view.mo && sel.d === d;
              const isToday = v === todayVal;
              const dis = disabledDay(d);
              return (
                <button key={i} type="button" disabled={dis} onClick={() => pick(view.y, view.mo, d)}
                  style={{
                    aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 7, fontSize: '.78rem', fontWeight: isSel ? 800 : 500, fontFamily: 'inherit',
                    cursor: dis ? 'not-allowed' : 'pointer', opacity: dis ? 0.3 : 1,
                    background: isSel ? 'var(--c-cyan,#00d4ff)' : 'transparent',
                    color: isSel ? '#06283d' : 'var(--c-text,#c8dff0)',
                    border: isToday && !isSel ? '1px solid var(--c-cyan,#00d4ff)' : '1px solid transparent',
                  }}
                  onMouseEnter={(e) => { if (!isSel && !dis) e.currentTarget.style.background = 'var(--c-surface2,#112538)'; }}
                  onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                  {d}
                </button>
              );
            })}
          </div>

          {/* Footer: Today / Clear */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '.6rem', paddingTop: '.5rem', borderTop: '1px solid var(--c-border,#1a3550)' }}>
            <button type="button" onClick={() => { fire(todayVal); setOpen(false); }} style={{ background: 'none', border: 'none', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', fontSize: '.74rem', fontWeight: 700, fontFamily: 'inherit' }}>{es ? 'Hoy' : 'Today'}</button>
            <button type="button" onClick={() => { fire(''); setOpen(false); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', fontSize: '.74rem', fontWeight: 600, fontFamily: 'inherit' }}><X size={12} /> {es ? 'Borrar' : 'Clear'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
